import { useMemo, useState } from 'react'
import { api, ApiError } from '../api/client'
import { exportHeaders, exportRequest, type ExportColumn } from '../lib/exportColumns'
import { STATION_TZ, formatDateTime } from '../lib/time'
import { projectExportName } from '../lib/projectForm'

interface Props {
  stations: string[]
  parameters: string[]
  /** What the file's columns will be, in the order it writes them. */
  columns: ExportColumn[]
  from: Date
  to: Date
  /** Set when the export is made from inside a project (Explore opened from
   *  the Projects page, `?project=`): the file is named after it. */
  project?: { name: string; station: string }
}

/** Exports exactly what is on the chart.
 *
 *  It resends the chart's own query with a format attached, which is what the
 *  POST body was shaped for. The alternative — a separate export form — lets
 *  the two drift, and a file that disagrees with the chart it was taken from
 *  is worse than no export at all.
 */
/*  Excel first, because most people here open the file in Excel and it is the
 *  only format that survives the trip intact: an .xlsx carries its own types,
 *  so a number is a number and a timestamp is a timestamp whatever the
 *  machine's locale says. A .csv is interpreted on arrival instead, and on a
 *  ka-GE machine Excel splits on ';' and reads ',' as the decimal point, which
 *  lands the strict comma file in a single column with every reading merged
 *  into the timestamp. Hence two CSV entries rather than one, each labelled by
 *  what it is for. */
type Format = 'xlsx' | 'csv-excel' | 'csv'

const FORMAT_LABEL: Record<Format, string> = {
  'xlsx': 'Excel (.xlsx)',
  'csv-excel': 'CSV for Excel (semicolon)',
  'csv': 'CSV (comma, for other tools)',
}

export function ExportPanel({ stations, parameters, columns, from, to, project }: Props) {
  const demo = import.meta.env.VITE_DEMO_MODE === 'true'
  const [format, setFormat] = useState<Format>(demo ? 'csv-excel' : 'xlsx')
  const [includeQuality, setIncludeQuality] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string>()

  const headers = useMemo(
    () => exportHeaders(columns, STATION_TZ, { includeQuality }),
    [columns, includeQuality])

  async function download() {
    setBusy(true)
    setError(undefined)
    try {
      // Station-local days, the last one the day the window's end falls in
      // (an end at midnight belongs to the day before).
      const filename = project && projectExportName(
        project.name, project.station, formatDateTime(from).slice(0, 10),
        formatDateTime(new Date(to.getTime() - 1)).slice(0, 10), format === 'xlsx' ? 'xlsx' : 'csv')
      await api.export(exportRequest({
        stations, parameters, from, to, format, includeQuality, filename: filename || undefined,
      }))
    } catch (cause) {
      // 413 carries the row count and how to narrow the request; it is the
      // most useful sentence the API produces, so it is shown verbatim.
      setError(cause instanceof ApiError ? cause.message : String(cause))
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="export">
      <h2>Export Data</h2>
      <div className="export-controls">
        <label className="field inline">
          <span>Format</span>
          <select value={format}
                  onChange={(e) => setFormat(e.target.value as Format)}>
            {(Object.keys(FORMAT_LABEL) as Format[]).map((value) => (
              <option key={value} value={value} disabled={demo && value === 'xlsx'}>{FORMAT_LABEL[value]}{demo && value === 'xlsx' ? ' — connected service only' : ''}</option>
            ))}
          </select>
        </label>

        <label className="check">
          <input type="checkbox" checked={includeQuality}
                 onChange={(e) => setIncludeQuality(e.target.checked)} />
          <span>Include quality flags</span>
        </label>

        <button className="button primary" onClick={download} disabled={busy || !columns.length || !(from < to)}>
          {busy ? 'Building…' : 'Download'}
        </button>
      </div>

      {/* The columns themselves rather than a count of them. A file is opened
          somewhere else, usually later, and "13 parameters" does not say what
          any of them will be called or in what order they arrive. Folded, so
          the panel stays as short as the board's above the list it sits on:
          a gateway selection runs to dozens of columns. */}
      <div className="export-preview">
        <details>
          <summary className="muted">
            {headers.length} column{headers.length === 1 ? '' : 's'}, in this order
          </summary>
          <ol className="column-list">
            {headers.map((header) => <li key={header}>{header}</li>)}
          </ol>
        </details>
        <small className="muted">
          {stations.join(', ')} · stored readings over the time window,
          one row per timestamp. Timestamps are station-local.
        </small>
      </div>

      {error && <p className="error">{error}</p>}
    </section>
  )
}
